import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import { getProductPhoto, getVialPhoto } from "@/lib/productPhotos";

// Best-seller card just below the hero. Admin-configurable via
// GET /api/site-settings/featured-best-seller, the API always returns a
// slug (with a valid server-side fallback on a fresh install), so we
// don't need to hardcode a fallback here. Card is skipped only while the
// underlying product query is in flight to avoid flashing an empty pill.
export default function HeroBuyCard() {
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();

  const { data: setting } = useQuery({
    queryKey: ["site-settings", "featured-best-seller"],
    queryFn: async () => (await api.get(`/site-settings/featured-best-seller`)).data,
  });
  const featuredSlug = setting?.slug;

  const { data } = useQuery({
    queryKey: ["product", featuredSlug],
    queryFn: async () => (await api.get(`/products/${featuredSlug}`)).data,
    enabled: !!featuredSlug,
  });
  const product = data?.product;
  if (!product) return null;

  // Resolve the best available product photo for whichever format the
  // admin selected. Pen kits have SEALED/OPEN/PEN photo tabs (PEN reads
  // best at card size); raw vials + vial kits publish a dedicated
  // vial hero. Fall through to any photo tab as a last resort so a
  // partially-photographed SKU still renders instead of a blank column.
  const photo =
    getVialPhoto(product.slug) ||
    getProductPhoto(product.slug, "PEN") ||
    getProductPhoto(product.slug, "SEALED") ||
    getProductPhoto(product.slug, "OPEN");

  return (
    <section className="border-b border-line" data-testid="hero-buy-card-section">
      <div className="container-pd pb-10 lg:pb-14">
        <div
          className="grid grid-cols-1 items-stretch overflow-hidden rounded-2xl panel-warm bg-white sm:grid-cols-[minmax(0,220px)_1fr]"
          data-testid="hero-buy-card"
        >
          <Link
            to={`/product/${product.slug}`}
            className="relative flex aspect-square items-center justify-center bg-white sm:aspect-auto"
          >
            <span
              className="absolute left-3 top-3 rounded-full bg-brandblue px-2.5 py-1 font-mono text-[10px] font-bold tracking-[0.12em] text-white"
              data-testid="hero-buy-card-badge"
            >
              BEST SELLER
            </span>
            {photo && (
              <img
                src={photo}
                alt={product.name}
                className="max-h-[240px] w-full object-contain p-6"
                // Homepage LCP image — fetch high so the browser prioritises
                // it over lazy card thumbnails further down the page.
                fetchpriority="high"
                decoding="async"
                draggable={false}
                data-testid="hero-buy-card-photo"
              />
            )}
          </Link>
          <div className="flex flex-col justify-between p-5 sm:p-6">
            <div>
              <p className="label-mono" data-testid="hero-buy-card-category">
                § {product.categories?.[0] || "Featured"}
              </p>
              <Link to={`/product/${product.slug}`}>
                <h2
                  className="mt-2 font-heading text-2xl font-black leading-tight text-ink sm:text-3xl"
                  data-testid="hero-buy-card-name"
                >
                  {product.name}
                </h2>
              </Link>
              <p
                className="mt-2 max-w-md text-sm text-subtle line-clamp-2"
                data-testid="hero-buy-card-description"
              >
                {product.short_description}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-subtle">
                <Star className="h-3.5 w-3.5 fill-brandblue text-brandblue" />
                <span>Pre-purchase COA · UK/EU tracked dispatch</span>
              </div>
            </div>
            <div className="mt-5 flex items-end justify-between gap-3">
              <div>
                <p className="text-xs text-subtle">From</p>
                <p
                  className="font-heading text-3xl font-black leading-none text-ink"
                  data-testid="hero-buy-card-price"
                >
                  {fmtPrice(product.price, { fractionDigits: 2 })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => addItem(product, 1)}
                className="btn-pill bg-brandblue px-6 py-3 text-sm font-bold text-white hover:bg-brandbluedark"
                data-testid="hero-buy-card-add"
              >
                ADD
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
